"use client";

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BackButton } from "@/components/ui/back-button";
import {
  Users,
  Briefcase,
  Zap,
  DollarSign,
  AlertTriangle,
  RefreshCw,
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
  Rocket,
  Clock,
  Shield,
} from "lucide-react";

type LaunchMetrics = {
  timestamp: string;
  workers: { today: number; yesterday: number; sevenDays: number; total: number };
  jobs: { today: number; yesterday: number; sevenDays: number; total: number };
  hires: { today: number; yesterday: number; sevenDays: number; total: number };
  payments: { charged: number; failed: number; total: number; revenue7d: number; successRate: number; errorRate: number };
  issues: { open: number; urgent: number };
  recentHires: { id: string; hiredAt: string; jobTitle: string; company: string }[];
  recentWorkers: { id: string; createdAt: string; trade: string; city: string }[];
};

function TrendIcon({ today, yesterday }: { today: number; yesterday: number }) {
  if (today > yesterday) return <TrendingUp className="h-4 w-4 text-green-600" />;
  if (today < yesterday) return <TrendingDown className="h-4 w-4 text-red-500" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

function MetricRow({
  label,
  icon: Icon,
  today,
  yesterday,
  sevenDays,
  total,
  highlight,
}: {
  label: string;
  icon: React.ElementType;
  today: number;
  yesterday: number;
  sevenDays: number;
  total: number;
  highlight?: boolean;
}) {
  return (
    <Card className={highlight && today > 0 ? "border-green-400 bg-green-50/50" : undefined}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/10">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground">{label}</p>
            <p className="text-xs text-muted-foreground">Total: {total}</p>
          </div>
          <TrendIcon today={today} yesterday={yesterday} />
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-muted/50 p-2">
            <p className="text-2xl font-bold">{today}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Today</p>
          </div>
          <div className="rounded-lg bg-muted/30 p-2">
            <p className="text-lg font-semibold text-muted-foreground">{yesterday}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Yesterday</p>
          </div>
          <div className="rounded-lg bg-muted/30 p-2">
            <p className="text-lg font-semibold text-muted-foreground">{sevenDays}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">7 Days</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PulsingDot({ color }: { color: string }) {
  return (
    <span className="relative flex h-2.5 w-2.5">
      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${color}`} />
      <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${color}`} />
    </span>
  );
}

export default function LaunchMetricsPage() {
  const [data, setData] = useState<LaunchMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(30);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/launch-metrics");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setError(null);
      setLastRefresh(new Date());
      setCountdown(30);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Countdown timer
  useEffect(() => {
    const tick = setInterval(() => {
      setCountdown((c) => (c <= 1 ? 30 : c - 1));
    }, 1000);
    return () => clearInterval(tick);
  }, [lastRefresh]);

  if (loading && !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading launch metrics...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-4 max-w-lg mx-auto">
        <Card>
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-8 w-8 text-destructive mx-auto mb-2" />
            <p className="text-destructive font-medium">{error}</p>
            <Button onClick={fetchData} variant="outline" className="mt-4">Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const hasActivity = data.workers.today > 0 || data.jobs.today > 0 || data.hires.today > 0;

  return (
    <div className="max-w-lg mx-auto p-4 pb-24 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackButton />
          <div>
            <h1 className="text-lg font-bold flex items-center gap-2">
              <Rocket className="h-5 w-5" />
              Launch Metrics
            </h1>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <PulsingDot color={hasActivity ? "bg-green-500" : "bg-yellow-500"} />
              <span>Live · refreshing in {countdown}s</span>
            </div>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Core Metrics */}
      <MetricRow
        label="Workers Joined"
        icon={Users}
        today={data.workers.today}
        yesterday={data.workers.yesterday}
        sevenDays={data.workers.sevenDays}
        total={data.workers.total}
        highlight
      />

      <MetricRow
        label="Jobs Posted"
        icon={Briefcase}
        today={data.jobs.today}
        yesterday={data.jobs.yesterday}
        sevenDays={data.jobs.sevenDays}
        total={data.jobs.total}
        highlight
      />

      <MetricRow
        label="Successful Hires"
        icon={Zap}
        today={data.hires.today}
        yesterday={data.hires.yesterday}
        sevenDays={data.hires.sevenDays}
        total={data.hires.total}
        highlight
      />

      {/* Payment Health */}
      <Card className={data.payments.errorRate > 10 ? "border-red-300" : undefined}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="h-4 w-4" /> Payment Health (7d)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center rounded-lg bg-green-50 p-3">
              <p className="text-2xl font-bold text-green-700">{data.payments.successRate}%</p>
              <p className="text-[10px] text-muted-foreground uppercase">Success Rate</p>
            </div>
            <div className="text-center rounded-lg bg-muted/50 p-3">
              <p className="text-2xl font-bold">${data.payments.revenue7d}</p>
              <p className="text-[10px] text-muted-foreground uppercase">Revenue</p>
            </div>
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-3 px-1">
            <span>{data.payments.charged} charged</span>
            <span>{data.payments.failed} failed</span>
            <span>{data.payments.total} total</span>
          </div>
          {data.payments.errorRate > 0 && (
            <div className="mt-2 rounded bg-red-50 p-2 text-xs text-red-700 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" />
              {data.payments.errorRate}% error rate — {data.payments.failed} failed payment{data.payments.failed !== 1 ? "s" : ""}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Issues Alert */}
      {(data.issues.open > 0 || data.issues.urgent > 0) && (
        <Card className={data.issues.urgent > 0 ? "border-red-400 bg-red-50/50" : "border-yellow-300"}>
          <CardContent className="p-4 flex items-center gap-3">
            <Shield className={`h-6 w-6 ${data.issues.urgent > 0 ? "text-red-600" : "text-yellow-600"}`} />
            <div>
              <p className="font-medium text-sm">
                {data.issues.urgent > 0
                  ? `🚨 ${data.issues.urgent} urgent issue${data.issues.urgent !== 1 ? "s" : ""}`
                  : `${data.issues.open} open issue${data.issues.open !== 1 ? "s" : ""}`}
              </p>
              <p className="text-xs text-muted-foreground">
                {data.issues.open} open total
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity Feed */}
      {(data.recentHires.length > 0 || data.recentWorkers.length > 0) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" /> Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.recentHires.map((hire) => (
              <div key={`hire-${hire.id}`} className="flex items-center gap-2 text-sm border-b pb-2 last:border-0">
                <Zap className="h-3.5 w-3.5 text-green-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="truncate"><span className="font-medium">Hire:</span> {hire.jobTitle}</p>
                  <p className="text-xs text-muted-foreground">{hire.company} · {timeAgo(hire.hiredAt)}</p>
                </div>
              </div>
            ))}
            {data.recentWorkers.map((worker) => (
              <div key={`worker-${worker.id}`} className="flex items-center gap-2 text-sm border-b pb-2 last:border-0">
                <Users className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="truncate"><span className="font-medium">New worker:</span> {worker.trade}</p>
                  <p className="text-xs text-muted-foreground">{worker.city} · {timeAgo(worker.createdAt)}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Footer */}
      <p className="text-center text-[10px] text-muted-foreground">
        Last updated: {lastRefresh?.toLocaleTimeString("en-AU")} · Auto-refresh: 30s
      </p>
    </div>
  );
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
